import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Wine, ChevronLeft, ChevronRight, Droplets } from 'lucide-react';
export const CulinarySection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const beverageItems = [{
    name: 'Sher-e-Punjab Lassi',
    type: 'Royal Beverage',
    description: 'Lion of Punjab Smoothie',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e7tNmw42aGJMhG2K8pg9sq/Sher-e-Punjab_Lassi_%28Lion_of_Punjab_Smoothie%29.webp',
    details: 'Named after Maharaja Ranjit Singh. Rich dahi and milk blend with saffron and honey, topped with malai foam and crushed nuts.'
  }, {
    name: "Hari Singh Nalwa's Tiger Fury",
    type: 'Royal Beverage',
    description: 'Fiery pomegranate and citrus cooler',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7s7gbBG7Kx151cwzpHK1du/Hari_Singh_Nalwa%E2%80%99s_Tiger_Fury_Sharbat.webp',
    details: "Inspired by the legendary general. Bold pomegranate and citrus with tamarind, ginger, roasted cumin — fierce as Nalwa's spirit."
  }, {
    name: 'Chabeel of Compassion',
    type: 'Royal Beverage',
    description: 'Rose & Kewra Cooler',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eKo28eax4tv8HfFsN3KZGY/Chabeel_of_Compassion_%28Rose__Kewra_Cooler%29.webp',
    details: 'Honoring the Sikh tradition of free service. Light rose-scented milk sherbet with kewra essence and floating rose petals.'
  }, {
    name: 'Shahi Aam Panna',
    type: 'Royal Beverage',
    description: 'Royal Raw Mango Punch',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uNnPb6Ex8V8D1T89ouiXSm/Shahi_Aam_Panna_%28Royal_Raw_Mango_Punch%29.webp',
    details: "Courtly refinement of Punjab's aristocracy. Roasted green mangoes with jaggery, mint, lime, toasted cumin and black salt."
  }, {
    name: "Pehalwan's Punch",
    type: 'Royal Beverage',
    description: 'Banana Jaggery Shake',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pMQJkiKcDmbaWHaNH1zaMn/Pehalwan%E2%80%99s_Punch_%28Banana_Jaggery_Shake%29.webp',
    details: 'Inspired by Punjabi wrestlers. Rich banana milkshake with yogurt, jaggery, cardamom and nutmeg, topped with honey and almonds.'
  }];
  useEffect(() => {
    if (!autoplay) return;
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % beverageItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoplay, beverageItems.length]);
  const next = () => {
    setAutoplay(false);
    setCurrentIndex(prevIndex => (prevIndex + 1) % beverageItems.length);
  };
  const prev = () => {
    setAutoplay(false);
    setCurrentIndex(prevIndex => (prevIndex - 1 + beverageItems.length) % beverageItems.length);
  };
  return <motion.section id="culinary" ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden" style={{
    opacity
  }} data-id="element-1103" data-mp="1riv59x48cb7o">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1104" data-mp="1co70kl6nlgt4"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1105" data-mp="1hikpq5uq2rpc">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1106" data-mp="13n0hq5gj7twp">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1107" data-mp="12wk5igo7l0rj">
            Liquid Heritage
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-1108" data-mp="xy8wu99ngwaw">
            ROYAL
            <br data-id="element-1109" data-mp="swjlesiimgma" />
            <span className="text-amber-400" data-id="element-1110" data-mp="sw9wl897ifi6">POURINGS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1111" data-mp="ov7tuhg2zlbg"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1112" data-mp="jwwl6a1ivgut">
            A curated journey through the drinks, aromas, and tastes that
            defined the court of Maharaja Ranjit Singh.
          </p>
        </motion.div>

        {/* Scent & Drink Design Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-6xl mx-auto mb-24" data-id="element-1113" data-mp="58sa9s8po9ml">
          <div className="border border-amber-400/30 p-8 md:p-12 bg-black/40" data-id="element-1114" data-mp="27unr4bzg7lfk">
            <div className="text-center mb-10" data-id="element-1115" data-mp="2csgz9akbapih">
              <div className="inline-flex items-center gap-3 mb-6" data-id="element-1116" data-mp="2gvvg23g2r9wz">
                <Droplets className="w-8 h-8 text-amber-400" data-id="element-1117" data-mp="2gtj0ty1pbdnm" />
                <h3 className="font-serif text-3xl md:text-4xl text-white" data-id="element-1118" data-mp="2ltnkty1tiqil">
                  Scent & <span className="text-amber-400" data-id="element-1119" data-mp="1p58uctqspigz">Drink Design</span>
                </h3>
              </div>
              <p className="text-lg text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1120" data-mp="1t72sovdiqadz">
                The sensory experience is not an afterthought—it is the bridge
                to the past. Each portal is accompanied by a historically
                researched beverage and a synchronized aromatic release,
                completing the illusion of time travel.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1121" data-mp="1ae8maptynqld">
              <div className="text-center border-l border-amber-400/10 pl-4" data-id="element-1122" data-mp="2dpxi94ru25sm">
                <h4 className="text-lg text-amber-400 mb-2 font-light" data-id="element-1123" data-mp="28rdvj25f0pz7">
                  Historical Recipes
                </h4>
                <p className="text-white/60 text-sm font-light" data-id="element-1124" data-mp="2mj0uj9qqsclo">
                  Drinks researched from court records and regional traditions
                </p>
              </div>
              <div className="text-center border-l border-amber-400/10 pl-4" data-id="element-1125" data-mp="1ur34nz43vbe1">
                <h4 className="text-lg text-amber-400 mb-2 font-light" data-id="element-1126" data-mp="1psi39wd4alo0">
                  Aromatic Pairing
                </h4>
                <p className="text-white/60 text-sm font-light" data-id="element-1127" data-mp="23ssy1ahjskeg">
                  Scent design layered with each pour to trigger memory and
                  emotion
                </p>
              </div>
              <div className="text-center border-l border-amber-400/10 pl-4" data-id="element-1128" data-mp="3f6n7v9pruvlq">
                <h4 className="text-lg text-amber-400 mb-2 font-light" data-id="element-1129" data-mp="3a8lo0mkius97">
                  Sensory Portal
                </h4>
                <p className="text-white/60 text-sm font-light" data-id="element-1130" data-mp="3ioaxcxnbtfr3">
                  Tastes that complete the visual and auditory immersion
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Beverage Showcase */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-5xl mx-auto mb-20" data-id="element-1131" data-mp="3dq9cc76cb6cz">
          <div className="text-center mb-12" data-id="element-1132" data-mp="3rpitb92de4i0">
            <div className="inline-flex items-center gap-3 mb-4" data-id="element-1133" data-mp="jhhuzeht8vu">
              <Wine className="w-6 h-6 text-amber-400" data-id="element-1134" data-mp="ho8hcchwak5" />
              <h3 className="font-serif text-3xl text-amber-400" data-id="element-1135" data-mp="o8ag52il7stm">
                A Tour of the Pourings
              </h3>
            </div>
            <p className="text-white/60 font-light" data-id="element-1136" data-mp="t24vxuv2nnnf">
              Flavors lost to time—now brought back to life
            </p>
          </div>

          <div className="relative" data-id="element-1137" data-mp="2kbyg589gjl50">
            <div className="border border-amber-400/30 overflow-hidden rounded-lg" data-id="element-1138" data-mp="2kax3x0gpgslp">
              <AnimatePresence mode="wait" data-id="element-1139" data-mp="2fe6lw7lcbmxn">
                <motion.div key={currentIndex} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.6
              }} data-id="element-1140" data-mp="2fb0zx180d165">
                  <div className="aspect-[16/9] relative" data-id="element-1141" data-mp="2u2jj17w5zbtq">
                    <img src={beverageItems[currentIndex].image} alt={beverageItems[currentIndex].name} className="w-full h-full object-cover" data-id="element-1142" data-mp="2u204801t93jx" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" data-id="element-1143" data-mp="3uag5waj6gvls"></div>
                    <div className="absolute top-6 left-6" data-id="element-1144" data-mp="33n69b4194jd">
                      <div className="bg-amber-400/90 backdrop-blur-sm px-4 py-2 border border-white/20" data-id="element-1145" data-mp="31smw18etkhe">
                        <span className="text-black text-xs font-medium tracking-wider uppercase" data-id="element-1146" data-mp="73xhb26oml53">
                          {beverageItems[currentIndex].type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-8 md:p-10 bg-black/80 space-y-4" data-id="element-1147" data-mp="lt3c8dyhlp76">
                    <h4 className="font-serif text-3xl md:text-4xl text-amber-400" data-id="element-1148" data-mp="qthlfzbtr56s">
                      {beverageItems[currentIndex].name}
                    </h4>
                    <p className="text-white/60 text-base font-light" data-id="element-1149" data-mp="2sl451ftdzxt8">
                      {beverageItems[currentIndex].description}
                    </p>
                    <div className="w-16 h-px bg-amber-400/30" data-id="element-1150" data-mp="2wn7rt0liaott"></div>
                    <p className="text-white/80 text-lg font-light leading-relaxed" data-id="element-1151" data-mp="2nta3mjwlxm9z">
                      {beverageItems[currentIndex].details}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>

              <button onClick={prev} className="absolute left-4 top-[30%] -translate-y-1/2 bg-black/80 hover:bg-amber-400 hover:text-black text-white p-3 transition-colors duration-300 border border-white/10" data-id="element-1152" data-mp="2wv9lfefg4udz">
                <ChevronLeft className="w-6 h-6" data-id="element-1153" data-mp="3arwjjkj4xqlh" />
              </button>
              <button onClick={next} className="absolute right-4 top-[30%] -translate-y-1/2 bg-black/80 hover:bg-amber-400 hover:text-black text-white p-3 transition-colors duration-300 border border-white/10" data-id="element-1154" data-mp="3fj64w8xni7rk">
                <ChevronRight className="w-6 h-6" data-id="element-1155" data-mp="3pfraowhhvmwg" />
              </button>
            </div>

            <div className="flex justify-center mt-8 gap-2" data-id="element-1156" data-mp="3uh6xou38g3hh">
              {beverageItems.map((_, index) => <button key={index} onClick={() => {
              setAutoplay(false);
              setCurrentIndex(index);
            }} className={`h-px transition-all duration-300 ${index === currentIndex ? 'w-12 bg-amber-400' : 'w-8 bg-white/30 hover:bg-white/50'}`} data-id="element-1157" data-mp="1irildgkmlz30" data-mpi={index} />)}
            </div>
          </div>
        </motion.div>

        {/* Closing Statement */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-4xl mx-auto" data-id="element-1158" data-mp="1wmb8p16rcjk5">
          <Droplets className="w-8 h-8 text-amber-400 mx-auto mb-6" data-id="element-1159" data-mp="2fk4au19k4pgh" />
          <h3 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-1160" data-mp="2fhrl31xk5asv">
            A Sensory <span className="text-amber-400" data-id="element-1161" data-mp="dn3u5f672iz5">Awakening</span>
          </h3>
          <p className="text-xl text-white/70 font-light leading-relaxed" data-id="element-1162" data-mp="ik3pow5f43c0">
            Every drink is paired with a specific portal and aromatic release.
            Experience the artistry of scent and taste that completes the
            illusion of 1825 Lahore.
          </p>
        </motion.div>
      </div>
    </motion.section>;
};